import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { format, addDays } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import { selectFilterData } from '../../features/mainFilter/selectors';
import { convertParamsToHash } from '../../utilities/urlResolver';
import { parseStringToJson } from "../../utilities/helpers";
import { hideOnboarding, setWizardDesktop } from '../../features/wizard-v2/onboardingSlice';
import { t } from '../../utilities/translator';
import { pushEvent } from '../../utilities/analytics';
import { LAST_SEARCH_CLICKED, LAST_SEARCH_WIZARD_SKIP } from '../../constants/analyticEvents';
import { getDateFnsLocale } from '../../utilities/localeUtils';
import { LOCALE } from '../../constants';

let wizardSkipSent = false;

const LastSearch = ({ showWizardModal, wizardSkip = false, filtersReady }) => {
  const dispatch = useDispatch();
  const { default_photo_url, locale } = window.holidayFinder;
  const heroArray = default_photo_url
    ? parseStringToJson(default_photo_url)[locale]
    : [{ photo: '' }];
  const [backgroundImage, setBackgroundImage] = useState(heroArray[0].photo || null);
  const mainFilter = useSelector(selectFilterData);
  const { where, what } = mainFilter.searchEngine;
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [selectedDestinationGroups, setSelectedDestinationGroups] = useState([]);
  const [selectedWhat, setSelectedWhat] = useState([]);
  const [searchTopic, setSearchTopic] = useState('');
  const [show, setShow] = useState(false);

  const hfLastFilters = JSON.parse(localStorage.getItem('hfLastFilters'));

  useEffect(() => {
    let timeout = null;
    if (!show && hfLastFilters && Object.keys(hfLastFilters).length && what && what.length) {
      timeout = setTimeout(() => {
        setShow(true);
      }, (wizardSkip ? 10 : 2700));
    }
    return () => clearTimeout(timeout);
  }, [what]);

  useEffect(() => {
    if (hfLastFilters && Object.keys(hfLastFilters).length) {
      if (hfLastFilters.engine?.where?.length && where?.destinations?.length) {
        const selDest = where.destinations.filter(dest => hfLastFilters.engine.where.indexOf(dest.id) > -1);
        setSelectedDestinations(selDest);
      }
      if (hfLastFilters.engine?.destinationGroups?.length) {
        setSelectedDestinationGroups(hfLastFilters.engine.destinationGroups);
      }
      if (hfLastFilters.engine?.what?.length && what?.length) {
        const selWhat = what.filter(wh => hfLastFilters.engine.what.indexOf(wh.id) > -1);
        setSelectedWhat(selWhat);
      }
    }
  }, [where, what]);

  useEffect(() => {
    if (selectedDestinations.length) {
      setBackgroundImage(selectedDestinations[0].photo || heroArray[0].photo);
    } else {
      setBackgroundImage(heroArray[0].photo);
    }
    if (selectedDestinations.length === 1) {
      setSearchTopic(`${selectedDestinations[0].name}, ${selectedDestinations[0].atlas_top}`);
    } else if (selectedDestinationGroups.length === 1) {
      setSearchTopic(selectedDestinationGroups[0].name);
    } else if (selectedWhat.length === 1) {
      setSearchTopic(selectedWhat[0].value);
    } else if (hfLastFilters?.engine?.when?.months?.periods?.length && hfLastFilters?.engine?.when?.months?.nights?.length) {
      let nightsTxt = '';
      if (hfLastFilters?.engine?.when?.months?.nights?.length) {
        if (hfLastFilters?.engine?.when?.months?.nights?.length > 1) {
          nightsTxt = `${hfLastFilters.engine.when.months.nights[0]}-${hfLastFilters.engine.when.months.nights[hfLastFilters.engine.when.months.nights.length - 1]}`;
        } else {
          nightsTxt = hfLastFilters.engine.when.months.nights[0];
        }
      }
      let monthTxt = '';
      if (hfLastFilters?.engine?.when?.months?.periods?.length) {
        monthTxt = format(new Date(hfLastFilters.engine.when.months.periods[0].start.split('/').reverse().join('-')), 'LLLL', { locale: getDateFnsLocale() })
      }
      let topicTxt = '';
      if (monthTxt && nightsTxt) {
        topicTxt = t('lastSearch.nightsInMonth', { nights: nightsTxt, month: monthTxt });
      } else if (nightsTxt) {
        topicTxt = t('lastSearch.yourLastSearchForNights', { nights: nightsTxt });
      } else if (monthTxt) {
        topicTxt = t('lastSearch.yourLastSearchInMonth', { month: monthTxt });
      }
      setSearchTopic(topicTxt);
    } else if (hfLastFilters?.engine?.when?.flexible?.start && hfLastFilters?.engine?.when?.flexible?.end) {
      const startDate = (LOCALE === 'de') ? hfLastFilters.engine.when.flexible.start.replaceAll('/', '.') : hfLastFilters.engine.when.flexible.start;
      const endDate = (LOCALE === 'de') ? hfLastFilters.engine.when.flexible.end.replaceAll('/', '.') : hfLastFilters.engine.when.flexible.end;
      const topicTxt = t('lastSearch.yourLastSearchForDates', { dates: `${startDate}-${endDate}` });
      setSearchTopic(topicTxt);
    } else if (hfLastFilters?.engine?.when?.specific?.start && hfLastFilters?.engine?.when?.specific?.end) {
      const startDate = (LOCALE === 'de') ? hfLastFilters.engine.when.specific.start.replaceAll('/', '.') : hfLastFilters.engine.when.specific.start;
      const endDate = (LOCALE === 'de') ? hfLastFilters.engine.when.specific.end.replaceAll('/', '.') : hfLastFilters.engine.when.specific.end;
      const topicTxt = t('lastSearch.yourLastSearchForDates', { dates: `${startDate}-${endDate}` });
      setSearchTopic(topicTxt);
    } else {
      setSearchTopic(t('lastSearch.yourLastSearch'));
    }
  }, [selectedDestinations.length, selectedDestinationGroups.length, selectedWhat.length]);

  const storageToUrlParams = (hfLastFilters) => {
    if (hfLastFilters && Object.keys(hfLastFilters).length) {
      const { adult, child, childAges, room } = hfLastFilters.engine.who;
      const typeFromStorage = Object.keys(hfLastFilters.engine.when)[0];
      let type = 'month';
      let months = [];
      let startDate = '';
      let endDate = '';
      let maxNights = '';
      let minNights = '';
      let nights = [];
      let weekendsOnly = false;

      switch(typeFromStorage) {
        case 'months':
          type = 'month';
          months = hfLastFilters.engine.when.months.periods.length ? hfLastFilters.engine.when.months.periods.map(m => +m.start.split('/')[1]) : [];
          nights = hfLastFilters.engine.when.months.nights;
          maxNights = hfLastFilters.engine.when.months.max;
          minNights = hfLastFilters.engine.when.months.min;
          weekendsOnly = hfLastFilters.engine.when.months.weekends_only;
          break;
        case 'flexible':
          type = 'range';
          startDate = hfLastFilters.engine.when.flexible.start?.replaceAll('/', '-');
          endDate = hfLastFilters.engine.when.flexible.end?.replaceAll('/', '-');
          nights = hfLastFilters.engine.when.flexible.nights;
          maxNights = hfLastFilters.engine.when.flexible.max;
          minNights = hfLastFilters.engine.when.flexible.min;
          weekendsOnly = hfLastFilters.engine.when.flexible.weekends_only;
          break;
        case 'specific':
          type = 'specific';
          startDate = hfLastFilters.engine.when.specific.start?.replaceAll('/', '-');
          endDate = hfLastFilters.engine.when.specific.end?.replaceAll('/', '-');
          break;
        default:
          type = 'month';
      }

      const dataForUrl = {
        fromwhere: hfLastFilters.fromwhere,
        adults: adult,
        children: child,
        childAges: childAges,
        rooms: room,
        destinations: hfLastFilters.engine.where,
        destinationsGroups: hfLastFilters.engine.destinationGroups.length ? hfLastFilters.engine.destinationGroups.map(dg => dg.id) : [],
        what: hfLastFilters.engine.what,
        type: type,
        facilities: hfLastFilters.filters.amenities,
        stops: hfLastFilters.filters.stops,
        stars: hfLastFilters.filters.rating,
        board: hfLastFilters.filters.board.map(b => b.toLowerCase()),
        luggage: Object.keys(hfLastFilters.filters.luggage).filter(bag => hfLastFilters.filters.luggage[bag])
      };

      if (type === 'month') {
        dataForUrl.months = months;
      }
      if (hfLastFilters.engine.budget && hfLastFilters.engine.budget.min && hfLastFilters.engine.budget.max) {
        dataForUrl.budget = [hfLastFilters.engine.budget.min, hfLastFilters.engine.budget.max];
      }
      if(startDate) {
        dataForUrl.startDate = startDate;
      }
      if(endDate) {
        dataForUrl.endDate = endDate;
      }
      if (maxNights) {
        dataForUrl.maxNights = maxNights;
      }
      if (minNights) {
        dataForUrl.minNights = minNights;
      }
      if (nights.length) {
        dataForUrl.nights = nights;
      }
      if (weekendsOnly) {
        dataForUrl.weekendsOnly = weekendsOnly;
      }
      if (hfLastFilters.filters.flex) {
        dataForUrl.flex = hfLastFilters.filters.flex;
      }
      if (hfLastFilters.filters.flight_duration) {
        dataForUrl.duration = hfLastFilters.filters.flight_duration;
      }
      if (hfLastFilters.specificHotel && hfLastFilters.specificHotel.hotel_id) {
        dataForUrl.hotelId = hfLastFilters.specificHotel.hotel_id;
      }

      return dataForUrl;
    }
    return {};
  };

  const moveToSearchUrl = (isWizardSkip) => {
    const dataForUrl = storageToUrlParams(hfLastFilters);
    const hashUrl = `#${convertParamsToHash(dataForUrl)}&order=-1&sort=best${isWizardSkip ? '&wizardSkip=true' : ''}`;
    window.location.href = hashUrl;
  };

  useEffect(() => {
    if (wizardSkip && what && what.length && hfLastFilters && Object.keys(hfLastFilters).length && !wizardSkipSent && filtersReady) {
      wizardSkipSent = true;
      moveToSearchUrl(true);
      dispatch(setWizardDesktop(false));
      dispatch(hideOnboarding());
      pushEvent(LAST_SEARCH_WIZARD_SKIP);
      document.getElementById('mobile-preload-container')?.remove();
      sessionStorage.removeItem('hideWhatTheme');
    }
  }, [wizardSkip, what, hfLastFilters, filtersReady]);

  if (!hfLastFilters || (hfLastFilters && !hfLastFilters.date)) {
    return '';
  }
  const dateNow = Date.now();
  const dateDiff = dateNow - hfLastFilters.date;
  const dayDiff = Math.round(dateDiff/(1000 * 60 * 60 * 24));
  const timeDiff = dateDiff/(1000 * 60 * 60);
  if (dayDiff > (wizardSkip ? 30 : 20)) {
    return '';
  }
  const today = new Date();
  if (hfLastFilters?.engine?.when?.months?.periods?.length) {
    const searchDate = new Date(hfLastFilters.engine.when.months.periods[0].end.split('/').reverse().join('-'));
    if (searchDate < today) {
      return '';
    }
  }
  if (hfLastFilters?.engine?.when?.flexible?.start) {
    const searchDate = new Date(hfLastFilters.engine.when.flexible.start.split('/').reverse().join('-'));
    if (searchDate < today) {
      return '';
    }
  }
  if (hfLastFilters?.engine?.when?.specific?.start) {
    const searchDate = new Date(hfLastFilters.engine.when.specific.start.split('/').reverse().join('-'));
    if (searchDate < today) {
      return '';
    }
  }

  const searchDayDate = format(new Date(hfLastFilters.date), 'dd/MM');
  const todayDate = format(new Date(), 'dd/MM');
  const yesterdayDate = format(addDays(new Date(), -1), 'dd/MM');

  let searchDate = t('lastSearch.fromDay', { day: format(new Date(hfLastFilters.date), "dd/MM") });
  if (searchDayDate === todayDate) {
    if (Math.round(timeDiff) > 0) {
      const hours = Math.round(timeDiff);
      if (hours === 1) {
        searchDate = t('lastSearch.fromHour', { time: hours });
      } else if (hours === 2) {
        searchDate = t('lastSearch.from2Hours', { time: hours });
      } else {
        searchDate = t('lastSearch.fromHours', { time: hours });
      }
    } else {
      const minutes = (Math.round(timeDiff * 60) === 0 ? 1 : Math.round(timeDiff * 60));
      if (minutes === 1) {
        searchDate = t('lastSearch.fromMinute', { time: minutes });
      } else {
        searchDate = t('lastSearch.fromMinutes', { time: minutes });
      }
    }
  } else if (searchDayDate === yesterdayDate) {
    searchDate = t('lastSearch.fromYesterday');
  } else if (dayDiff > 1 && dayDiff <= 6) {
    searchDate = t('lastSearch.fromDay', { day: format(new Date(hfLastFilters.date), 'EEEE', { locale: getDateFnsLocale() }) });
  } else if (dayDiff > 6) {
    searchDate = t('lastSearch.fromDaysAgo', { days: dayDiff });
  }

  const getPassengersLabel = (hfLastFilters) => {
    let passTxt = '';
    const who = hfLastFilters?.engine?.who;
    if (who?.adult) {
      const babies = who.childAges.filter(age => age < 2).length;
      const children = who.child - babies;
      passTxt = `${who.adult} ${t("general.adults_plural", { count: who.adult })}${
        children > 0
          ? `, ${children} ${t("general.children_plural", { count: children })},`
          : ""
      } ${
        babies > 0
          ? `, ${babies} ${t("general.infants_plural", { count: babies })},`
          : ""
      }`;
    }
    return passTxt;
  }

  const handleClick = () => {
    if (wizardSkip) {
      return;
    }
    moveToSearchUrl();
    dispatch(setWizardDesktop(false));
    dispatch(hideOnboarding());
    pushEvent(LAST_SEARCH_CLICKED);
    document.getElementById('mobile-preload-container')?.remove();
    sessionStorage.removeItem('hideWhatTheme');
  };

  const lastSearch = (
    <div className={classNames("last-search-wrap", { 'last-search-show': show, 'hidden': showWizardModal })}>
      <div className="last-search-title">{t('lastSearch.title')}</div>
      <div className="last-search" onClick={handleClick}>
        {backgroundImage && (
          <div className="last-search-img" style={{ backgroundImage: `url(${backgroundImage})` }}></div>
        )}
        <div className="last-search-main">
          <div className="lsm-l">
            {!wizardSkip && <span className="last-search-title mb-3">{t('lastSearch.title')}</span>}
            <b>{searchTopic}</b>
            <span>{searchDate}</span>
            {wizardSkip && <span>{getPassengersLabel(hfLastFilters)}</span>}
          </div>
          <span className="lsm-arrow"><i className="ri-arrow-right-s-line" /></span>
        </div>
      </div>
    </div>
  )

  return lastSearch;
};

export default LastSearch;
